/**
 * Limite calculado do tomador e do grupo economico, razao de consumo e liberacao e
 * contragarantia da operacao (C-LIM).
 *
 * Arquivo proprio do modulo `creditline` do backend. Ver
 * docs/ADR/0088-limites-exposicao-e-contragarantias.md e docs/API.md, secao 7.54.
 *
 * Nada aqui libera emissao: so a dimensao "calculavel" tem disponivel para comparar, e
 * sem metrica de consumo definida (LIM-Q01 a LIM-Q08) toda dimensao e "nao verificavel".
 */

import type { Money } from './comum';

import { api } from '../client';

export interface CreditLineReason {
  code: string;
  message: string;
}

export type CreditLineSituation = 'calculavel' | 'nao_verificavel';

export type CreditLineDimensionKind =
  'grupo_de_modalidade' | 'limite_global' | 'grupo_economico' | 'grupo_economico_total';

export interface CreditLineDimension {
  dimension: CreditLineDimensionKind;
  modality_group?: string;
  economic_group_id?: string;
  economic_group_name?: string;
  situation: CreditLineSituation;
  effect: 'segue' | 'revisao' | 'suspenso' | 'analise';
  reasons: CreditLineReason[];
  configured: boolean;
  approved: Money | null;
  informed_used: Money | null;
  calculated_used: Money | null;
  used: Money | null;
  reserved: Money | null;
  available: Money | null;
  excess: Money | null;
  valid_until: string | null;
  validity: 'vigente' | 'vencido' | 'nao_definida' | 'sem_vencimento' | '';
}

export interface CreditLineLine {
  modality_group: string;
  situation: CreditLineSituation;
  dimensions: CreditLineDimension[];
}

export interface CreditLinePolicy {
  metric: string;
  reserves_proposals: boolean | null;
  releases_on_cancellation: boolean | null;
  releases_on_coverage_end: boolean | null;
  releases_on_reduction: boolean | null;
  increase_consumes: boolean | null;
  global_limit_applies: boolean | null;
  validity_rule: string;
  validity_months: number | null;
  expired_effect: string;
  holder_rule_homologated: boolean;
  group_rule_homologated: boolean;
}

export interface CreditLineTotal {
  count: number;
  amount: Money;
}

export interface CreditLineExposureLine {
  modality_group: string;
  open_guarantees: CreditLineTotal;
  open_bank_guarantees: CreditLineTotal;
  expired_guarantees: CreditLineTotal;
  cancelled_guarantees: CreditLineTotal;
  proposals_in_progress: CreditLineTotal;
  proposals_credit_approved: CreditLineTotal;
}

export interface CreditLineExposure {
  lines: CreditLineExposureLine[];
  total: CreditLineExposureLine;
  by_metric: { used: Money; reserved: Money } | null;
  reasons: CreditLineReason[];
}

export interface PolicyholderCreditLine {
  policyholder_id: string;
  reference_at: string;
  /** O SeguraCore calcula o utilizado: ha versao ativa e a metrica esta completa. */
  controlled: boolean;
  metric: string;
  parameter_set_id: string | null;
  parameter_version: number;
  missing: CreditLineReason[];
  policy: CreditLinePolicy;
  /** Limite e taxa ocultos a corretora com cartao de credito ou PEP aberto (PM-09). */
  hidden_from_broker: boolean;
  lines: CreditLineLine[];
  /** So para quem le a ficha; nunca para a corretora. */
  exposure: CreditLineExposure | null;
}

export interface CreditLineMovement {
  id: string;
  modality_group: string;
  kind: 'consumo' | 'liberacao';
  event:
    | 'emissao'
    | 'cancelamento'
    | 'endosso_aumento'
    | 'endosso_reducao'
    | 'carga_inicial'
    | 'fim_de_vigencia';
  amount: Money;
  operation_id: string | null;
  policy_id: string | null;
  policy_number: number;
  policy_external_number: string;
  endorsement_id: string | null;
  cancellation_id: string | null;
  reason: string;
  metric: string;
  parameter_version: number | null;
  verification:
    'conferido' | 'excederia_em_sombra' | 'nao_conferido' | 'liberacao' | 'carga';
  enforced: boolean;
  approved: Money | null;
  used_before: Money | null;
  reserved_before: Money | null;
  available_before: Money | null;
  available_after: Money | null;
  not_verifiable_reasons: string[];
  actor_label: string;
  created_at: string;
  /** Linha calculada, e nao gravada (o fim da vigencia). */
  calculated: boolean;
}

export interface CreditLineMovements {
  items: CreditLineMovement[];
  hidden_from_broker: boolean;
}

export interface EconomicGroupCreditLine {
  economic_group_id: string;
  name: string;
  document: string;
  reference_at: string;
  controlled: boolean;
  metric: string;
  missing: CreditLineReason[];
  members: string[];
  dimensions: CreditLineDimension[];
  exposure: CreditLineExposure;
}

export interface CounterGuaranteeCriterion {
  criterion: 'acumulo' | 'classe' | 'modalidade' | 'limite';
  rule: string;
  result: 'exige' | 'nao_exige' | 'nao_verificavel';
  message: string;
  reasons: CreditLineReason[];
  compared: Money | null;
  threshold: Money | null;
  economic_group_id: string | null;
}

export interface OperationCounterGuarantee {
  operation_id: string;
  policyholder_id: string;
  status: 'nao_exigida' | 'exigida_formalizada' | 'pendente' | 'nao_verificavel';
  pending: boolean;
  rule_homologated: boolean;
  criteria: CounterGuaranteeCriterion[];
  reasons: CreditLineReason[];
  operation_ccg_exists: boolean;
  operation_ccg_formalized: boolean;
  holder_contract_signed: boolean;
  holder_ignores_ccg_signature: boolean;
  insurer_signature_bypass: boolean;
  accepted_kinds: string[];
  coverage_bps: Record<string, number>;
}

export const creditLine = {
  ofPolicyholder: (policyholderId: string, signal?: AbortSignal) =>
    api.get<PolicyholderCreditLine>(
      `/api/v1/policyholders/${policyholderId}/credit-line`,
      signal,
    ),
  movements: (policyholderId: string, signal?: AbortSignal) =>
    api.get<CreditLineMovements>(
      `/api/v1/policyholders/${policyholderId}/credit-line/movements`,
      signal,
    ),
  ofEconomicGroup: (groupId: string, signal?: AbortSignal) =>
    api.get<EconomicGroupCreditLine>(
      `/api/v1/economic-groups/${groupId}/credit-line`,
      signal,
    ),
  counterGuarantee: (operationId: string, signal?: AbortSignal) =>
    api.get<OperationCounterGuarantee>(
      `/api/v1/operations/${operationId}/counter-guarantee`,
      signal,
    ),
};
